import { type FileLike } from "interfaces";
import { type Form, type WizardFormBase } from "../../../../../interfaces/Form";
import { type VideoFeedbackPageTask } from "features/BackgroundTasks/taskPool";
import { type ScheduleConfiguration } from "../types/models";
import { type Dispatch, type SetStateAction } from "react";
import { type PriorityLevels } from "enums";

interface CampaignWizardStep extends WizardFormBase {
  entityId?: number;
  subscribeOnDiscarded?: (subscriber: () => void) => void;
  unSubscribeOnDiscarded?: (subscriber: () => void) => void;
}

export interface ConfigureProps extends CampaignWizardStep {
  isReadOnlyView?: boolean;
}

export interface EmailTemplateProps extends CampaignWizardStep {
  isEdit?: boolean;
}

export interface LandingPageProps extends CampaignWizardStep {}

export interface FeedbackPageProps extends CampaignWizardStep {}

export interface SendsProps extends WizardFormBase {
  campaignId?: number;
  isSendDisabled: boolean;
}

export interface CampaignFormProps extends Form {
  isLoadingOptions: boolean;
}

export interface EmailTemplateFormProps extends Form {
  isLoaded: boolean;
}

export interface LandingPageFormProps extends Form {
  isLoaded: boolean;
}

export interface FeedbackPageFormProps extends Form {
  handleFileChange: (files: FileList) => void;
  uploadFilesHandler: (files: FileList) => Promise<void>;
  task: VideoFeedbackPageTask;
}

export interface RemedialTrainingFormFields {
  triggerTypeId?: number;
  flowId?: number;
  flowTitle: string;
  priority: PriorityLevels;
  fixedDueDate: string;
  isFixedDueDate: boolean;
  daysToComplete: number;
  thumbnailUrl: string;
}

export interface CampaignFormFields {
  id?: number;
  name: string;
  campaignTypeId?: number;
  subDomainId?: number;
  labels: string[];
  remedialTraining: RemedialTrainingFormFields;
}

export interface EmailTemplateFormFields {
  subject: string;
  previewText: string;
  senderName: string;
  senderEmail: string;
  html: string;
  contentUrlWithSas: string;
}

export interface BodyContent {
  html: string;
  contentUrlWithSas: string;
}

export interface LandingPageFormFields {
  html: string;
  contentUrlWithSas: string;
}

export interface FeedbackPageFormFields {
  videoUrl: string;
  uploadedVideos: FileLike[];
  message: string;
}

export interface CampaignOption {
  id: number;
  name: string;
}

export interface ScheduleProps {
  useTimeZone: boolean;
  configuration: ScheduleConfiguration;
  onScheduleChange: Dispatch<SetStateAction<ScheduleConfiguration>>;
}

// Start Threat Defense Phishing Campaign types

type ThreatDefenseBase = {
  EmailsSent: number;
  EmailsOpened: number;
  EmailsClicked: number;
  DataSubmitted: number;
};

export type ThreatDefenseReportLineChart = {
  Date: string;
} & ThreatDefenseBase;

export type ThreatDefenseReportEngagement = ThreatDefenseBase;

export type ThreatDefenseReportCards = ThreatDefenseBase;

export enum ThreatDefenseSessionStatusEnum {
  SCHEDULED = "Scheduled",
  IN_PROGRESS = "In Progress",
  COMPLETED = "Completed",
  CANCELED = "Canceled",
}

export type ThreatDefenseReportTable = {
  AccountId: number;
  AccountName: string;
  CampaignRunId: number;
  CampaignRunStart: string;
  EmailsClicked: number;
  EmailsOpened: number;
  EmailsSent: number;
  Status: ThreatDefenseSessionStatusEnum;
  TimeZone: "UTC" | string; // IANA timezone formats
};

export enum ThreatDefenseEmailStatusEnum {
  SENT = "Sent",
  OPENED = "Opened",
  CLICKED = "Clicked",
  SUBMITTED = "Submitted",
}

export interface ThreatDefenseReportTablePeople {
  UserId: number;
  AccountId: number;
  UserName: string;
  JobTitle: string;
  Department: string;
  Email: string;
  Status: ThreatDefenseEmailStatusEnum;
}

// End Threat Defense Phishing Campaign types

// Start Remedial Training types

export type ThreatDefenseFlowStatus =
  | ThreatDefenseFlowStatusEnum.NOT_STARTED
  | ThreatDefenseFlowStatusEnum.IN_PROGRESS
  | ThreatDefenseFlowStatusEnum.COMPLETED;

export enum ThreatDefenseFlowStatusEnum {
  NOT_STARTED = "Not Started",
  IN_PROGRESS = "In Progress",
  COMPLETED = "Completed",
}

export interface RemedialTrainingLineChartItem {
  Date: string;
  TotalStarts: number;
  TotalCompletes: number;
  TotalNotStarted: number;
}

export interface RemedialTrainingEngagementResponse {
  TotalStarts: number;
  TotalCompletes: number;
  TotalNotStarted: number;
}

export interface RemedialTrainingUserItem {
  AccountId: number;
  UserId: number;
  UserName: string;
  Email: string;
  Department: string;
  JobTitle: string;
  CampaignId: number;
  CampaignTitle: string;
  LastProgress: string | null;
  Status: ThreatDefenseFlowStatus;
}

// End Remedial Training types
